// src/pages/Promotions/PromotionsPage.jsx — العروض الترويجية (خصومات)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, IconButton, Tooltip, CircularProgress, Switch, MenuItem,
  Dialog, DialogContent, DialogActions, Autocomplete, Chip, InputAdornment, FormControlLabel,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import PercentIcon from "@mui/icons-material/Percent";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import axios from "../../axios";
import { ReportPage, fieldSx, fmtMoney, fmtNumber, BRAND } from "../../components/Reports/ReportKit";

// ───────────────────────── التعريفات ─────────────────────────
export const PROMO_TYPES = [
  { value: 1, label: "خصم نسبة", desc: "نسبة من سعر الأصناف المختارة", icon: <PercentIcon />, color: "#4f46e5" },
  { value: 2, label: "خصم مبلغ", desc: "مبلغ ثابت لكل وحدة أساسية", icon: <PaymentsOutlinedIcon />, color: "#0891b2" },
  { value: 3, label: "سعر خاص", desc: "سعر جديد للوحدة الأساسية خلال العرض", icon: <SellOutlinedIcon />, color: "#059669" },
  { value: 4, label: "اشترِ واحصل", desc: "اشترِ كمية واحصل على كمية مجانًا أو بخصم", icon: <CardGiftcardOutlinedIcon />, color: "#d97706" },
  { value: 5, label: "خصم الفاتورة", desc: "خصم على إجمالي الفاتورة عند تجاوز مبلغ", icon: <ReceiptLongOutlinedIcon />, color: "#db2777" },
];
const typeOf = (v) => PROMO_TYPES.find(t => t.value === Number(v)) ?? PROMO_TYPES[0];

const STATUS = {
  active: { label: "نشط", bg: "#d1fae5", fg: "#047857" },
  outOfHours: { label: "خارج وقت العرض", bg: "#e0f2fe", fg: "#0369a1" },
  scheduled: { label: "مجدول", bg: "#fef3c7", fg: "#b45309" },
  expired: { label: "منتهي", bg: "#f1f5f9", fg: "#64748b" },
  paused: { label: "موقوف", bg: "#ffe4e6", fg: "#be123c" },
};
const STATUS_FILTERS = [
  { value: "", label: "كل الحالات" },
  { value: "active", label: "النشطة" },
  { value: "scheduled", label: "المجدولة" },
  { value: "expired", label: "المنتهية" },
  { value: "paused", label: "الموقوفة" },
];
const DAYS = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
// أنواع الحركات المخزنية ليست مبيعات — لا عروض عليها
const NON_SALES_TYPES = [7, 8, 9, 12, 13, 99];

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660)));
  return Number.isFinite(n) ? n : 0;
};
const errMsg = (e, fb) => {
  const d = e?.response?.data;
  return (typeof d === "string" && d) || d?.message || d?.title || fb;
};

/** وصف مختصر للعرض: "خصم 15%"، "اشترِ 2 واحصل على 1 مجانًا" ... */
export function describePromo(p) {
  const v = num(p.value);
  switch (Number(p.type)) {
    case 1: return `خصم ${fmtNumber(v)}%`;
    case 2: return `خصم ${fmtMoney(v)} لكل وحدة`;
    case 3: return `السعر ${fmtMoney(v)} للوحدة`;
    case 4: {
      const g = num(p.getDiscountPercent) >= 100 ? "مجانًا" : `بخصم ${fmtNumber(p.getDiscountPercent)}%`;
      return `اشترِ ${fmtNumber(p.buyQty)} واحصل على ${fmtNumber(p.getQty)} ${g}`;
    }
    case 5: {
      const val = p.isPercent ? `${fmtNumber(v)}%` : fmtMoney(v);
      return `خصم ${val} على الفاتورة${num(p.minInvoiceAmount) > 0 ? ` عند ${fmtMoney(p.minInvoiceAmount)} فأكثر` : ""}`;
    }
    default: return "";
  }
}

const primaryBtnSx = {
  fontWeight: 800, borderRadius: "10px", px: 2, boxShadow: "none",
  bgcolor: "#4f46e5 !important", color: "#fff !important", border: "1px solid #4f46e5 !important",
  "&:hover": { bgcolor: "#4338ca !important", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#c7d2fe !important", color: "#fff !important", borderColor: "#c7d2fe !important" },
  "& .MuiButton-startIcon": { ml: .5, mr: -.25, color: "inherit" },
};
const secondaryBtnSx = {
  fontWeight: 700, borderRadius: "10px", px: 2, boxShadow: "none",
  bgcolor: "#fff !important", color: "#334155 !important", border: "1px solid #cbd5e1 !important",
  "&:hover": { bgcolor: "#f1f5f9 !important" },
  "&.Mui-disabled": { bgcolor: "#fff !important", color: "#94a3b8 !important", borderColor: "#e2e8f0 !important" },
  "& .MuiButton-startIcon": { ml: .5, mr: -.25, color: "inherit" },
};

const Pill = ({ bg, fg, children, icon }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: .5, px: 1.1, py: .3, borderRadius: "999px", bgcolor: bg, color: fg, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap", "& svg": { fontSize: 15 } }}>
    {icon}{children}
  </Box>
);

// ───────────────────────── الصفحة ─────────────────────────
export default function PromotionsPage() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);   // null | {} جديد | عرض
  const [lookups, setLookups] = useState({ products: [], categories: [], types: [], branches: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/Promotions");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errMsg(e, "تعذر تحميل العروض"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      axios.get(`/Product/by-company/${companyId}`).catch(() => ({ data: [] })),
      axios.get(`/Category/by-company/${companyId}`).catch(() => ({ data: [] })),
      axios.get(`/InvoiceTypes?companyId=${companyId}`).catch(() => ({ data: [] })),
      axios.get(`/Branch/by-company/${companyId}`).catch(() => ({ data: [] })),
    ]).then(([p, c, t, b]) => setLookups({
      products: (Array.isArray(p.data) ? p.data : []).filter(x => !x.is_deleted && !x.isDeleted),
      categories: Array.isArray(c.data) ? c.data : [],
      types: (Array.isArray(t.data) ? t.data : []).filter(x => !(x.is_deleted ?? x.isDeleted) && !NON_SALES_TYPES.includes(Number(x.invoiceTypeId))),
      branches: Array.isArray(b.data) ? b.data : [],
    }));
  }, [companyId]);

  const typeName = useCallback((id) => lookups.types.find(t => Number(t.invoiceTypeId) === Number(id))?.name ?? `نوع ${id}`, [lookups.types]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter(p =>
      (!status || p.status === status || (status === "active" && p.status === "outOfHours")) &&
      (!q || String(p.name).toLowerCase().includes(q) || describePromo(p).toLowerCase().includes(q)));
  }, [list, search, status]);

  const stats = useMemo(() => ({
    active: list.filter(p => p.status === "active" || p.status === "outOfHours").length,
    scheduled: list.filter(p => p.status === "scheduled").length,
    expired: list.filter(p => p.status === "expired").length,
    discount: list.reduce((s, p) => s + num(p.totalDiscount), 0),
  }), [list]);

  const toggle = async (p) => {
    try {
      const { data } = await axios.post(`/Promotions/${p.id}/toggle`);
      setList(l => l.map(x => (x.id === p.id ? { ...x, ...data } : x)));
    } catch (e) {
      toast.error(errMsg(e, "تعذر تغيير حالة العرض"));
    }
  };

  const remove = async (p) => {
    const ask = await Swal.fire({
      icon: "warning", title: "حذف العرض", html: `حذف العرض <b>${String(p.name).replace(/</g, "&lt;")}</b>؟<br/><small>الفواتير السابقة تبقى كما هي.</small>`,
      showCancelButton: true, confirmButtonText: "حذف", cancelButtonText: "إلغاء", confirmButtonColor: "#e11d48", reverseButtons: true, focusCancel: true,
    });
    if (!ask.isConfirmed) return;
    try {
      await axios.delete(`/Promotions/${p.id}`);
      setList(l => l.filter(x => x.id !== p.id));
    } catch (e) {
      toast.error(errMsg(e, "تعذر حذف العرض"));
    }
  };

  const th = { fontWeight: 800, color: "#475569", fontSize: 13, py: 1.25, px: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e5e7eb", textAlign: "right", whiteSpace: "nowrap" };
  const td = { py: 1.25, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" };

  return (
    <ReportPage
      title="العروض الترويجية"
      subtitle="خصومات تُطبَّق تلقائيًا على أنواع الفواتير التي تختارها"
      icon={<LocalOfferOutlinedIcon />}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: load, disabled: loading },
        { label: "عرض جديد", icon: <AddIcon />, onClick: () => setEditing({}), primary: true },
      ]}
      stats={[
        { label: "عروض نشطة", value: fmtNumber(stats.active), icon: <PlayCircleOutlineIcon />, color: "success" },
        { label: "مجدولة", value: fmtNumber(stats.scheduled), icon: <ScheduleIcon />, color: "warning" },
        { label: "منتهية", value: fmtNumber(stats.expired), icon: <EventBusyOutlinedIcon />, color: "slate" },
        { label: "إجمالي الخصومات الممنوحة", value: fmtMoney(stats.discount), icon: <SavingsOutlinedIcon />, color: "primary", highlight: true },
      ]}
    >
      <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", p: 1.25, borderBottom: "1px solid #e5e7eb", bgcolor: "#fafbfc" }}>
          <TextField size="small" value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث باسم العرض..."
            sx={{ ...fieldSx, flex: "1 1 240px", maxWidth: 420 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8" }} /></InputAdornment> }} />
          <TextField select size="small" value={status} onChange={e => setStatus(e.target.value)} sx={{ ...fieldSx, flex: "0 1 180px" }}
            SelectProps={{ displayEmpty: true }}>
            {STATUS_FILTERS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={30} /></Box>
        ) : shown.length === 0 ? (
          <Box sx={{ py: 7, textAlign: "center", color: "#94a3b8" }}>
            <LocalOfferOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: "#64748b" }}>{list.length ? "لا توجد عروض مطابقة" : "لا توجد عروض بعد"}</Typography>
            {!list.length && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})} sx={{ ...primaryBtnSx, mt: 1.5 }}>إنشاء أول عرض</Button>
            )}
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 960 }}>
              <thead>
                <tr>
                  <Box component="th" sx={th}>العرض</Box>
                  <Box component="th" sx={th}>الشمول</Box>
                  <Box component="th" sx={th}>الفواتير</Box>
                  <Box component="th" sx={th}>الفترة</Box>
                  <Box component="th" sx={th}>الحالة</Box>
                  <Box component="th" sx={th}>الاستخدام</Box>
                  <Box component="th" sx={{ ...th, textAlign: "center" }}>تفعيل</Box>
                  <Box component="th" sx={th}></Box>
                </tr>
              </thead>
              <tbody>
                {shown.map(p => {
                  const t = typeOf(p.type);
                  const st = STATUS[p.status] ?? STATUS.expired;
                  const scope = p.allProducts ? "كل الأصناف"
                    : [p.productIds?.length ? `${p.productIds.length} صنف` : null, p.categoryIds?.length ? `${p.categoryIds.length} مجموعة` : null].filter(Boolean).join(" + ");
                  return (
                    <Box component="tr" key={p.id} sx={{ "&:hover": { bgcolor: "#fafbff" } }}>
                      <Box component="td" sx={td}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Box sx={{ width: 38, height: 38, borderRadius: "11px", display: "grid", placeItems: "center", bgcolor: `${t.color}14`, color: t.color, flexShrink: 0 }}>{t.icon}</Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>{p.name}</Typography>
                            <Typography sx={{ fontSize: 12.5, color: t.color, fontWeight: 700 }}>{describePromo(p)}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...td, fontSize: 13, color: "#475569", fontWeight: 700 }}>{scope || "—"}</Box>
                      <Box component="td" sx={td}>
                        <Box sx={{ display: "flex", gap: .5, flexWrap: "wrap", maxWidth: 220 }}>
                          {(p.invoiceTypes || []).map(id => <Pill key={id} bg="#eef2ff" fg="#4338ca">{typeName(id)}</Pill>)}
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...td, fontSize: 12.5, color: "#475569", whiteSpace: "nowrap", direction: "ltr", textAlign: "right" }}>
                        {dayjs(p.startDate).format("DD/MM/YYYY")} → {dayjs(p.endDate).format("DD/MM/YYYY")}
                        {(p.startTime && p.endTime) && <Box sx={{ color: "#94a3b8" }}>{p.startTime} - {p.endTime}</Box>}
                      </Box>
                      <Box component="td" sx={td}><Pill bg={st.bg} fg={st.fg}>{st.label}</Pill></Box>
                      <Box component="td" sx={{ ...td, fontSize: 12.5, whiteSpace: "nowrap" }}>
                        <Box sx={{ fontWeight: 800 }}>{fmtNumber(p.usedInvoices)} فاتورة</Box>
                        <Box sx={{ color: "#64748b", direction: "ltr", textAlign: "right" }}>{fmtMoney(p.totalDiscount)}</Box>
                      </Box>
                      <Box component="td" sx={{ ...td, textAlign: "center" }}>
                        <Switch size="small" checked={!!p.isActive} onChange={() => toggle(p)} disabled={p.status === "expired" && !p.isActive} />
                      </Box>
                      <Box component="td" sx={{ ...td, whiteSpace: "nowrap" }}>
                        <Tooltip title="تعديل"><IconButton size="small" onClick={() => setEditing(p)} sx={{ color: "#d97706" }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="نسخ كعرض جديد">
                          <IconButton size="small" onClick={() => setEditing({ ...p, id: 0, name: `${p.name} (نسخة)`, isActive: false })} sx={{ color: "#475569" }}>
                            <ContentCopyOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف"><IconButton size="small" onClick={() => remove(p)} sx={{ color: "#e11d48" }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        )}
      </Paper>

      {editing && (
        <PromotionDialog
          promo={editing}
          lookups={lookups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </ReportPage>
  );
}

// ───────────────────────── محرّر العرض ─────────────────────────
const toLocalInput = (d) => (d ? dayjs(d).format("YYYY-MM-DDTHH:mm") : "");

function emptyPromo(types) {
  const sales = types.filter(t => [1, 11].includes(Number(t.invoiceTypeId))).map(t => Number(t.invoiceTypeId));
  return {
    id: 0, name: "", description: "", type: 1, value: "", isPercent: true,
    buyQty: 2, getQty: 1, getDiscountPercent: 100,
    minQty: "", minInvoiceAmount: "", maxDiscount: "",
    allProducts: false, productIds: [], categoryIds: [],
    invoiceTypes: sales.length ? sales : [1],
    branches: [],
    startDate: dayjs().startOf("day").format("YYYY-MM-DDTHH:mm"),
    endDate: dayjs().add(1, "month").endOf("day").format("YYYY-MM-DDTHH:mm"),
    daysOfWeek: [], startTime: "", endTime: "", priority: 0, isActive: true,
  };
}

const SectionTitle = ({ children, hint }) => (
  <Box sx={{ mt: 2.25, mb: 1 }}>
    <Typography sx={{ fontWeight: 800, fontSize: 14.5, color: "#0f172a" }}>{children}</Typography>
    {hint && <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{hint}</Typography>}
  </Box>
);

function PromotionDialog({ promo, lookups, onClose, onSaved }) {
  const isEdit = !!promo?.id;
  const [f, setF] = useState(() => (promo && (promo.id || promo.name)
    ? { ...emptyPromo(lookups.types), ...promo, startDate: toLocalInput(promo.startDate), endDate: toLocalInput(promo.endDate), startTime: promo.startTime || "", endTime: promo.endTime || "" }
    : emptyPromo(lookups.types)));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const type = Number(f.type);

  const productOptions = lookups.products;
  const selectedProducts = productOptions.filter(p => f.productIds.includes(p.id));
  const selectedCategories = lookups.categories.filter(c => f.categoryIds.includes(c.id));
  const selectedBranches = lookups.branches.filter(b => f.branches.includes(b.id));

  const valueLabel = { 1: "نسبة الخصم %", 2: "مبلغ الخصم لكل وحدة", 3: "السعر الخاص للوحدة", 5: f.isPercent ? "نسبة الخصم %" : "مبلغ الخصم" }[type];

  const save = async () => {
    if (!f.name.trim()) { toast.error("أدخل اسم العرض"); return; }
    if (!f.invoiceTypes.length) { toast.error("اختر نوع فاتورة واحدًا على الأقل"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        type,
        value: num(f.value), buyQty: num(f.buyQty), getQty: num(f.getQty), getDiscountPercent: num(f.getDiscountPercent) || 100,
        minQty: num(f.minQty), minInvoiceAmount: num(f.minInvoiceAmount), maxDiscount: num(f.maxDiscount), priority: num(f.priority),
        startDate: f.startDate, endDate: f.endDate,
        startTime: f.startTime || null, endTime: f.endTime || null,
      };
      if (isEdit) await axios.put(`/Promotions/${f.id}`, payload);
      else await axios.post("/Promotions", payload);
      toast.success(isEdit ? "تم تعديل العرض" : "تم إنشاء العرض");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "تعذر حفظ العرض"));
    } finally {
      setSaving(false);
    }
  };

  const toggleIn = (k, v) => set(k, f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v]);
  const numField = (k, label, extra = {}) => (
    <TextField size="small" label={label} value={f[k]} onChange={e => set(k, e.target.value)}
      inputProps={{ inputMode: "decimal", dir: "ltr", style: { textAlign: "right" } }} sx={fieldSx} {...extra} />
  );

  const preview = describePromo({ ...f, type, value: num(f.value) });

  return (
    <Dialog open onClose={() => !saving && onClose()} dir="rtl" maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.5, py: 1.75, bgcolor: BRAND.primary, color: "#fff" }}>
        <LocalOfferOutlinedIcon />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{isEdit ? "تعديل العرض" : "عرض جديد"}</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: .85 }}>{preview || "اختر نوع العرض وقيمته"}</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ pt: "14px !important" }}>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1.3fr 1fr" } }}>
          <TextField size="small" autoFocus label="اسم العرض" value={f.name} onChange={e => set("name", e.target.value)} sx={fieldSx} />
          <TextField size="small" label="وصف (اختياري)" value={f.description || ""} onChange={e => set("description", e.target.value)} sx={fieldSx} />
        </Box>

        <SectionTitle>نوع العرض</SectionTitle>
        <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, 1fr)" } }}>
          {PROMO_TYPES.map(t => {
            const on = type === t.value;
            return (
              <Box key={t.value} role="button" tabIndex={0} onClick={() => set("type", t.value)}
                onKeyDown={e => { if (e.key === "Enter") set("type", t.value); }}
                sx={{
                  p: 1.25, borderRadius: "12px", cursor: "pointer", textAlign: "center",
                  border: `2px solid ${on ? t.color : "#e5e7eb"}`, bgcolor: on ? `${t.color}10` : "#fff",
                  transition: "all .15s", "&:hover": { borderColor: t.color },
                }}>
                <Box sx={{ color: t.color, "& svg": { fontSize: 26 } }}>{t.icon}</Box>
                <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: on ? t.color : "#0f172a" }}>{t.label}</Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.4 }}>{t.desc}</Typography>
              </Box>
            );
          })}
        </Box>

        <SectionTitle hint={type === 2 || type === 3 ? "المبالغ بنفس أساس أسعار بطاقة الصنف (شاملة الضريبة إذا كان السعر شاملًا في إعدادات نوع الفاتورة)" : undefined}>
          قيمة العرض
        </SectionTitle>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, alignItems: "start" }}>
          {type === 4 ? (
            <>
              {numField("buyQty", "اشترِ (كمية)")}
              {numField("getQty", "واحصل على (كمية)")}
              {numField("getDiscountPercent", "خصم الكمية المُهداة %", { helperText: "100 = مجانًا" })}
            </>
          ) : (
            <>
              {type === 5 && (
                <TextField select size="small" label="نوع الخصم" value={f.isPercent ? "p" : "a"} onChange={e => set("isPercent", e.target.value === "p")} sx={fieldSx}>
                  <MenuItem value="p">نسبة %</MenuItem>
                  <MenuItem value="a">مبلغ</MenuItem>
                </TextField>
              )}
              {numField("value", valueLabel)}
              {(type === 1 || type === 5) && numField("maxDiscount", "أقصى خصم (اختياري)", { helperText: "فارغ = بدون حد" })}
            </>
          )}
          {type !== 5 ? numField("minQty", "حد أدنى للكمية (بالوحدة الأساسية)", { helperText: "اختياري" })
            : numField("minInvoiceAmount", "حد أدنى لإجمالي الفاتورة", { helperText: "اختياري" })}
        </Box>

        <SectionTitle hint={type === 5 ? "الخصم يُحسب على قيمة الأصناف المشمولة فقط" : undefined}>الأصناف المشمولة</SectionTitle>
        <FormControlLabel control={<Switch checked={!!f.allProducts} onChange={e => set("allProducts", e.target.checked)} />} label="كل الأصناف" />
        {!f.allProducts && (
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mt: .5 }}>
            <Autocomplete multiple size="small" options={lookups.categories} value={selectedCategories}
              getOptionLabel={o => o.nameAr || o.name || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => set("categoryIds", v.map(x => x.id))}
              renderInput={p => <TextField {...p} label="المجموعات" sx={fieldSx} />} />
            <Autocomplete multiple size="small" options={productOptions} value={selectedProducts}
              getOptionLabel={o => `${o.arabicName || ""}${o.code ? ` — ${o.code}` : ""}`} isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                return (q ? opts.filter(o => `${o.arabicName} ${o.code} ${o.barcode}`.toLowerCase().includes(q)) : opts).slice(0, 100);
              }}
              onChange={(_, v) => set("productIds", v.map(x => x.id))}
              renderInput={p => <TextField {...p} label="الأصناف" placeholder="بحث بالاسم أو الكود أو الباركود" sx={fieldSx} />} />
          </Box>
        )}

        <SectionTitle hint="العرض يُطبَّق تلقائيًا على هذه الأنواع فقط">أنواع الفواتير</SectionTitle>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: .75 }}>
          {lookups.types.map(t => {
            const id = Number(t.invoiceTypeId);
            const on = f.invoiceTypes.includes(id);
            return (
              <Chip key={id} label={t.name} onClick={() => toggleIn("invoiceTypes", id)}
                sx={{ fontWeight: 800, borderRadius: "10px", border: `1.5px solid ${on ? "#4f46e5" : "#e2e8f0"}`, bgcolor: on ? "#eef2ff !important" : "#fff !important", color: on ? "#4338ca" : "#64748b" }} />
            );
          })}
        </Box>

        <SectionTitle>الفترة والفروع</SectionTitle>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1.4fr" } }}>
          <TextField size="small" type="datetime-local" label="من" value={f.startDate} onChange={e => set("startDate", e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField size="small" type="datetime-local" label="إلى" value={f.endDate} onChange={e => set("endDate", e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <Autocomplete multiple size="small" options={lookups.branches} value={selectedBranches}
            getOptionLabel={o => o.nameAr || o.name || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => set("branches", v.map(x => x.id))}
            renderInput={p => <TextField {...p} label="الفروع" placeholder={selectedBranches.length ? "" : "كل الفروع"} sx={fieldSx} />} />
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: .75, mt: 1.25, alignItems: "center" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b", ml: .5 }}>الأيام:</Typography>
          {DAYS.map((d, i) => {
            const on = f.daysOfWeek.includes(i);
            return (
              <Chip key={i} size="small" label={d} onClick={() => toggleIn("daysOfWeek", i)}
                sx={{ fontWeight: 700, border: `1.5px solid ${on ? "#4f46e5" : "#e2e8f0"}`, bgcolor: on ? "#eef2ff !important" : "#fff !important", color: on ? "#4338ca" : "#64748b" }} />
            );
          })}
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{f.daysOfWeek.length ? "" : "(كل الأيام)"}</Typography>
        </Box>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, mt: 1.5 }}>
          <TextField size="small" type="time" label="من الساعة" value={f.startTime} onChange={e => set("startTime", e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField size="small" type="time" label="إلى الساعة" value={f.endTime} onChange={e => set("endTime", e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx}
            helperText={!f.startTime && !f.endTime ? "فارغ = طوال اليوم" : (
              <Box component="span" role="button" onClick={() => setF(p => ({ ...p, startTime: "", endTime: "" }))}
                sx={{ color: "#4f46e5", fontWeight: 800, cursor: "pointer", textDecoration: "underline" }}>
                طوال اليوم (مسح الوقت)
              </Box>
            )} />
          {numField("priority", "الأولوية", { helperText: "عند تساوي الخصم يُقدَّم الأعلى" })}
          <FormControlLabel sx={{ mt: -2 }} control={<Switch checked={!!f.isActive} onChange={e => set("isActive", e.target.checked)} />} label="مفعّل" />
        </Box>

        {f.startTime && f.endTime && (
          <Box sx={{ mt: 1.5, p: 1.25, borderRadius: "10px", bgcolor: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: 13, fontWeight: 700 }}>
            ⏰ العرض يعمل فقط من الساعة {f.startTime} إلى {f.endTime} — خارج هذا الوقت لا يظهر في الفواتير.
          </Box>
        )}
        <Box sx={{ mt: 2, p: 1.5, borderRadius: "12px", bgcolor: "#f8fafc", border: "1px dashed #cbd5e1", fontSize: 13, color: "#475569", lineHeight: 1.9 }}>
          <b>قواعد التطبيق:</b> يُطبَّق على كل سطر أفضل عرض أصناف واحد، ثم أفضل عرض فاتورة على الإجمالي.
          يُضاف خصم العرض فوق الخصم اليدوي ولا يتجاوز قيمة السطر. الخادم يعيد الحساب عند الحفظ.
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: "1px solid #eef0f4" }}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={secondaryBtnSx}>إلغاء</Button>
        <Button variant="contained" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null} sx={primaryBtnSx}>
          {isEdit ? "حفظ التعديل" : "إنشاء العرض"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}